import {FormControl, FormGroup} from '@angular/forms';

import {BaseControl, SelectControl} from 'appShared/classes/controls';

import {
    AddNewFeedBundleAlaricInternalPriceSelector,
    AddNewFeedBundleControls,
    AddNewFeedBundleCostToAlaricNonProSelector,
    AddNewFeedBundleCostToAlaricProSelector,
    AddNewFeedBundleCurrencySelector,
    AddNewFeedBundleDescriptionSelector,
    AddNewFeedBundleIndividualPriceNonProSelector,
    AddNewFeedBundleIndividualPriceProSelector,
    AddNewFeedBundleNameSelector,
    AddNewFeedBundlePlatformFrontendIdSelector
} from 'appAssets/descriptors';
import {
    AuthApiService,
    FeedPriceTypesEnum,
    HttpStatusCodesEnum,
    MarketDataFeedBundleInputModel,
    MarketDataFeedBundleInterface,
    MarketDataFeedBundleModel,
    PlatformFrontendInterface,
    ServerResponse,
    ServerResponseData,
    SesHelper,
    SesService
} from '@spa/api';
import {Helper} from 'appShared/classes';
import {map} from 'rxjs/operators';
import {MarketDataFeedsFormErrorStatesEnum} from 'appEntries/panel/enums';
import {SelectOptionInterface} from 'appShared/components';
import {BaseMarketDataForm} from 'appShared/forms';
import { CurrencyTrait } from 'appEntries/panel/traits';
import { InArrayValidator } from '@spa/validators';
import { CurrencyService } from 'appShared/services';

const ONLY_ONE_PFE_SELECTION_LENGTH = 2;

export class UpdateExistingBundleForm extends BaseMarketDataForm {
    private bundle: MarketDataFeedBundleModel;
    private readonly platformFrontends: PlatformFrontendInterface[];
    private readonly authenticationService: AuthApiService;
    private readonly currencyService: CurrencyService;
    public platformFrontendsAutoComplete: SelectOptionInterface[];

    constructor(
        authenticationService: AuthApiService,
        sesService: SesService,
        currencyService: CurrencyService,
        platformFrontends: PlatformFrontendInterface[],
        bundle: MarketDataFeedBundleModel
    ) {
        super(sesService);
        this.authenticationService = authenticationService;
        this.currencyService = currencyService;
        this.platformFrontends = platformFrontends;
        this.bundle = bundle;
    }

    private updateMarketDataFeed(): ServerResponse<boolean | MarketDataFeedBundleModel> {
        const clonedModel = this.bundle.serialize();

        const alaricProCost = this.getFormPriceValueByKey(AddNewFeedBundleCostToAlaricProSelector);
        const alaricNonProCost = this.getFormPriceValueByKey(AddNewFeedBundleCostToAlaricNonProSelector);
        const individualPricePro = this.getFormPriceValueByKey(AddNewFeedBundleIndividualPriceProSelector);
        const individualPriceNonPro = this.getFormPriceValueByKey(AddNewFeedBundleIndividualPriceNonProSelector);
        const alaricInternalPrice = this.getFormPriceValueByKey(AddNewFeedBundleAlaricInternalPriceSelector);
        const description = this.formGroup.value[AddNewFeedBundleDescriptionSelector];
        const name = this.formGroup.value[AddNewFeedBundleNameSelector];
        const currency = this.formGroup.value[AddNewFeedBundleCurrencySelector];
        const platformFrontendId = this.formGroup.value[AddNewFeedBundlePlatformFrontendIdSelector];

        const feedIds = this.bundle.feedList.map(feed => feed.id);

        const inputModel = new MarketDataFeedBundleInputModel(
            name,
            description,
            currency,
            individualPricePro,
            individualPriceNonPro,
            alaricInternalPrice,
            alaricNonProCost,
            alaricProCost,
            feedIds,
            platformFrontendId
        );

        return this.sesService.updateMarketDataFeedBundle(this.bundle.id, inputModel).pipe(
            map((registerResponse: ServerResponseData<MarketDataFeedBundleInterface>) => {
                if (registerResponse.status !== HttpStatusCodesEnum.Ok) {
                    this.bundle = new MarketDataFeedBundleModel(clonedModel);

                    return Helper.errorsHelper().createErrorResponse<MarketDataFeedBundleInterface>(
                        registerResponse,
                        MarketDataFeedsFormErrorStatesEnum.UpdateMarketDataFeedFailed
                    );
                }

                const data = registerResponse.data as MarketDataFeedBundleInterface;

                if (data !== null) {
                    this.bundle = new MarketDataFeedBundleModel(data);
                }

                return Helper.createSuccessResponse<MarketDataFeedBundleModel>(this.bundle);
            })
        );
    }

    private initControl(control: BaseControl<any>): BaseControl {
        switch (control.name) {
            case AddNewFeedBundleNameSelector:
                control.value = this.bundle.name;
                break;
            case AddNewFeedBundleDescriptionSelector:
                control.value = this.bundle.description;
                break;
            case AddNewFeedBundlePlatformFrontendIdSelector:
                control.value = this.bundle.platformFrontend?.id;
                break;
            case AddNewFeedBundleAlaricInternalPriceSelector:
                control.value = SesHelper.getPriceDataBy(this.bundle.priceList, FeedPriceTypesEnum.AlaricInternal).price;
                break;
            case AddNewFeedBundleCostToAlaricProSelector:
                control.value = SesHelper.getPriceDataBy(this.bundle.priceList, FeedPriceTypesEnum.AlaricPro).price;
                break;
            case AddNewFeedBundleCostToAlaricNonProSelector:
                control.value = SesHelper.getPriceDataBy(this.bundle.priceList, FeedPriceTypesEnum.AlaricNonPro).price;
                break;
            case AddNewFeedBundleIndividualPriceProSelector:
                control.value = SesHelper.getPriceDataBy(this.bundle.priceList, FeedPriceTypesEnum.SelfPro).price;
                break;
            case AddNewFeedBundleIndividualPriceNonProSelector:
                control.value = SesHelper.getPriceDataBy(this.bundle.priceList, FeedPriceTypesEnum.SelfNonPro).price;
                break;
            case AddNewFeedBundleCurrencySelector:
                control.value = SesHelper.getPriceCurrency(this.bundle.priceList);
                 break;
            default:
                control.value = '';
                break;
        }

        return control;
    }

    private preparePlatformFrontendData(): void {
        if (!this.platformFrontends) {
            return;
        }

        this.platformFrontendsAutoComplete = [{
            label: 'No Platform Frontend',
            value: null
        }];

        const selectedPlatformFrontendIds = this.bundle.feedList.map(feed => feed?.platformFrontend?.id);
        const platformFrontendList = selectedPlatformFrontendIds && selectedPlatformFrontendIds.length > 0 ?
            this.platformFrontends.filter(platformFrontend => selectedPlatformFrontendIds.includes(platformFrontend.id)) :
            this.platformFrontends;

        const selectOptions = platformFrontendList.map((item: PlatformFrontendInterface): SelectOptionInterface => {
            return {
                label: `${item.name}`,
                value: item.id
            };
        });

        this.platformFrontendsAutoComplete.push(...selectOptions);

        const control = this.controls[2] as SelectControl;
        control.options = this.platformFrontendsAutoComplete;
        control.disabled = this.platformFrontendsAutoComplete.length === ONLY_ONE_PFE_SELECTION_LENGTH;
    }

    private prepareCurrencyData(): void {
        const currencies = []; 
        const control = this.controls[8] as SelectControl;
        control.options = CurrencyTrait.getSortedCurrencyAsOptions(this.currencyService.getAllCurrencies());
        currencies.push(...this.currencyService.getAllCurrencies().map(x => x.currencyCode)); 
        control.extendValidators(InArrayValidator(currencies));  
        control.disabled = control.options.length === 1;
    }
     

    initForm(): void {
        this.formGroup = new FormGroup({});
        const feedControls = AddNewFeedBundleControls();

        this.controls = feedControls.map((control: BaseControl) => {
            if (this.bundle) {
                control = this.initControl(control);
            }

            this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });

        this.preparePlatformFrontendData();
        this.prepareCurrencyData();
    }

    createRequest(): ServerResponse<boolean | MarketDataFeedBundleModel> | null {
        if (!this.formGroup.dirty) {
            return null;
        }

        return this.updateMarketDataFeed();
    }
}
